import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  deleteAssistantSessionsBySessionId,
  getAssistantSessions,
} from '@/api/generated/assistant/assistant';
import type { GetAssistantSessions200 } from '@/api/generated/lucioAPI.schemas';
import { queryKeys } from '@/config/query/queryKeys';

interface UseSessionsHistoryOptions {
  limit?: number;
  search?: string;
}

export const useSessionsHistory = ({ limit = 10, search }: UseSessionsHistoryOptions) => {
  return useInfiniteQuery<
    GetAssistantSessions200,
    Error,
    { pageParams: Array<number>; pages: Array<GetAssistantSessions200> },
    ReadonlyArray<unknown>,
    number
  >({
    enabled: true,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      if (pagination.page < pagination.totalPages) {
        return pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      getAssistantSessions({
        limit,
        page: pageParam,
        ...(search ? { search } : {}),
      }),
    queryKey: [...queryKeys.chat.sessions(search), limit],
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => deleteAssistantSessionsBySessionId(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.all });
    },
    retry: 1,
    retryDelay: 1000,
  });
};
